import { CalendarIcon } from '@chakra-ui/icons';
import {
    Box,
    InputGroup,
    InputProps,
    useMultiStyleConfig,
} from '@chakra-ui/react';
import { isString } from '@common/util/types';
import { isValidDateString } from '@common/validation';
import { CalendarDate, parseDate } from '@internationalized/date';
import { FC, useEffect, useRef } from 'react';
import { AriaDatePickerProps, useDatePicker } from 'react-aria';
import { useDatePickerState } from 'react-stately';
import { Calendar } from './Calendar';
import { DateField } from './DateField';
import { Dialog } from './Dialog';
import { FieldButton } from './FieldButton';
import { Popover } from './Popover';

export type DatePickerProps = Pick<InputProps, 'variant' | 'size'> & {
    id?: string;
    name?: string;
    value: string;
    onChange: (value: string) => void;
    onValidationChange?: (isValid: boolean) => void;
    onBlur: (event: React.FocusEvent<Element>) => void;
    options?: DatePickerOptions;
};

export type DatePickerOptions = AriaDatePickerProps<CalendarDate>;

const DatePicker: FC<DatePickerProps> = ({
    id,
    name,
    value,
    onChange,
    onBlur,
    onValidationChange,
    options,
    variant,
    size,
}) => {
    // Use an uncontrolled component internally
    const state = useDatePickerState({
        defaultValue: isValidDateString(value) ? parseDate(value) : undefined,
        onChange: (value) => {
            // Avoid returning invalid dates through event handler
            if (
                !value ||
                value.year < (options?.minValue?.year ?? 1900) ||
                value.year > (options?.maxValue?.year ?? 2100)
            ) {
                onChange('');
                return;
            }

            onChange(value.toString());
        },
        onBlur,
        isOpen: options?.isOpen,
        onOpenChange: options?.onOpenChange,
    });

    const ref = useRef(null);
    const { groupProps, fieldProps, buttonProps, dialogProps, calendarProps } =
        useDatePicker<CalendarDate>(
            { 'aria-label': 'Date', ...options },
            state,
            ref
        );

    const styles = useMultiStyleConfig('Input', { variant, size });

    // If `value` prop changes, update internal state like a controlled component
    useEffect(() => {
        if (isValidDateString(value) && value !== state.value?.toString()) {
            state.setDateValue(parseDate(value));
        }
        // N.B. Only run this when the `value` prop changes!
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
        <Box position="relative" flexDirection="column" w="full">
            <input type="hidden" id={id} name={name} value={value} />
            {/* @ts-expect-error Chakra's type-mapping is a bit wonky here. We're only using a few of these types, and know they play nicely. */}
            <InputGroup
                {...styles.field}
                {...groupProps}
                ref={ref}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
            >
                <DateField
                    {...fieldProps}
                    {...(isString(styles.field.fontSize) && {
                        fontSize: styles.field.fontSize,
                    })}
                    onValidationChange={onValidationChange}
                    /* @ts-expect-error Chakra's type-mapping is a bit wonky here. */
                    fontSize={styles?.field?.fontSize}
                />
                <FieldButton {...buttonProps}>
                    <CalendarIcon />
                </FieldButton>
            </InputGroup>
            {state.isOpen && (
                <Popover
                    triggerRef={ref}
                    state={state}
                    placement="bottom start"
                >
                    <Dialog {...dialogProps}>
                        <Calendar {...calendarProps} />
                    </Dialog>
                </Popover>
            )}
        </Box>
    );
};

export default DatePicker;
